// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: when a channel's chat is on screen for Bump (bump-plan.md section 3), as in Obyrus (section 4): from
// the time its message list mounts until the user leaves it by any route, which is the list unmounting (another
// channel, DMs, another page) or a full-page view opening over it (user, guild or channel settings: upstream's
// background modals). Leaving ends the banner and consumes the record; coming back is a new opening, which shows
// nothing for a consumed record. Window focus doesn't count (tabbing away keeps the banner), nor does a dialog.
// Exclude from upstream sync.

import ChannelBumps from '@app/features/bump/state/ChannelBumps';
import ReadStates from '@app/features/read_state/state/ReadStates';
import Modal from '@app/features/ui/state/Modal';
import {reaction} from 'mobx';
import {useEffect} from 'react';

/** A full-page view is open over the app: upstream's background modals (user, guild and channel settings). */
export function isPageOverChat(): boolean {
	return Modal.modals.some((entry) => entry.isBackground);
}

/** Opens the channel for Bump while its chat is on screen. Call it before the auto-ack effect: opening may hold it. */
export function useBumpChatOnScreen(channelId: string): void {
	useEffect(() => {
		let onScreen = false;
		const dispose = reaction(
			() => !isPageOverChat(),
			(visible) => {
				if (visible === onScreen) return;
				onScreen = visible;
				if (visible) ChannelBumps.handleChannelOpened(channelId, ReadStates.hasUnread(channelId));
				else ChannelBumps.handleChannelLeft(channelId);
			},
			{fireImmediately: true},
		);
		return () => {
			dispose();
			if (onScreen) ChannelBumps.handleChannelLeft(channelId);
		};
	}, [channelId]);
}
